// src/pages/FinanceiroPage.js
//
// Módulo Financeiro — Cobranças
// - Lê os chamados com valor a cobrar no banco (default) (somente leitura; não altera chamados nem chama APIs).
// - Mantém o controle das cobranças no banco "financ", coleção "cobrancas" (ID do documento = ID do chamado).
// - Etapas: A Cobrar → Cobrado → Recebido (ou Cancelado).

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    collection, onSnapshot, doc, getDoc, updateDoc, writeBatch, arrayUnion, serverTimestamp
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, dbFinanc, auth, storage } from '../App';
import { appId } from '../config/firebaseConfig';
import { ADMIN_EMAIL } from '../config/permissionsConfig';
import { printFinanceReport } from '../utils/reports';
import DocumentosFiscais from '../components/DocumentosFiscais';
import { gerarDanfsePdf } from '../utils/danfse';

const COBRANCAS = 'cobrancas';

export const ETAPAS = {
    a_faturar: { label: 'A Faturar', color: 'text-violet-300',  bg: 'bg-violet-500/10',  border: 'border-violet-500/30',  bar: 'bg-violet-400' },
    a_cobrar:  { label: 'A Cobrar',  color: 'text-amber-300',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   bar: 'bg-amber-400' },
    cobrado:   { label: 'Cobrança Enviada', color: 'text-cyan-300',    bg: 'bg-cyan-500/10',    border: 'border-cyan-500/30',    bar: 'bg-cyan-400' },
    recebido:  { label: 'Recebido',  color: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', bar: 'bg-emerald-400' },
    cancelado: { label: 'Cancelado', color: 'text-slate-400',   bg: 'bg-slate-500/10',   border: 'border-slate-500/30',   bar: 'bg-slate-500' }
};
const ETAPA_ORDER = ['a_faturar', 'a_cobrar', 'cobrado', 'recebido', 'cancelado'];
const DIAS_ALERTA_FATURAR = 7; // dias em "A Faturar" até destacar como atrasado
const NEXT_ETAPA = { a_cobrar: 'cobrado', cobrado: 'recebido' };
const DATE_FIELD_BY_ETAPA = { cobrado: 'dataCobranca', recebido: 'dataRecebimento', cancelado: 'dataCancelamento' };

const TIPO_LABEL = { com_nota: 'Com NF', sem_nota: 'Sem NF', sem_cobranca: 'Contrato', a_faturar: 'A definir' };

// Situação no VHSYS (mesmas cores da coluna "Situação" do VHSYS)
const VHSYS_SIT = {
    aberto:    { label: 'Em Aberto', dot: 'bg-slate-400' },
    atrasado:  { label: 'Atrasado',  dot: 'bg-yellow-400' },
    liquidado: { label: 'Liquidado', dot: 'bg-emerald-500' },
    cancelado: { label: 'Cancelado', dot: 'bg-red-500' }
};
const NOTA_STATUS = {
    rascunho: 'NFS-e ainda em rascunho',
    aguardando_lancar_contas: 'NF emitida — falta "Lançar contas" no VHSYS',
    contas_lancadas: 'Contas lançadas — aguardando vínculo',
    varias_contas_candidatas: 'Várias contas possíveis — vincule manualmente',
    nao_encontrada: 'NFS-e não encontrada no VHSYS'
};
const FORMAS_PAGAMENTO_PADRAO = [
    'PIX', 'Boleto', 'Transferência', 'Ted', 'Dinheiro', 'Cartão de Crédito', 'Cartão de Débito', 'Cheque',
    'Depósito Identificado', 'Depósito em C/C', 'Débito Automático', 'Pagamento online', 'Outros'
];
const PREF_CONTA = 'arcus_fin_ultima_conta';
const PREF_FORMA = 'arcus_fin_ultima_forma';
const callFn = async (name, data) => (await httpsCallable(getFunctions(), name)(data)).data;

/** Só a forma "Boleto" gera boleto no VHSYS (mesma regra da Cloud Function). */
const usaBoleto = (forma) => !forma || /boleto/i.test(String(forma));
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const separarEmails = (texto) => String(texto || '').split(/[\s,;]+/).map(e => e.trim().toLowerCase()).filter(Boolean);

/** Campo de e-mails em "chips" (Enter, vírgula ou ponto e vírgula adicionam). */
function EmailChips({ value, onChange, input, onInput, placeholder, erro }) {
    const adicionar = (texto) => {
        const lista = separarEmails(texto);
        if (!lista.length) return;
        const invalidos = lista.filter(e => !EMAIL_RE.test(e));
        const validos = lista.filter(e => EMAIL_RE.test(e));
        onChange([...new Set([...value, ...validos])], invalidos.join(' '));
    };
    return (
        <div className={`w-full bg-slate-900 border ${erro ? 'border-red-500/60' : 'border-slate-700'} rounded-lg p-2 flex flex-wrap items-center gap-1.5 focus-within:ring-1 focus-within:ring-cyan-500`}>
            {value.map(e => (
                <span key={e} className="inline-flex items-center gap-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 text-xs font-semibold rounded-md px-2 py-0.5">
                    {e}
                    <button type="button" onClick={() => onChange(value.filter(x => x !== e), input)} className="text-cyan-300/70 hover:text-red-300 font-bold" title="Remover">×</button>
                </span>
            ))}
            <input
                type="text"
                value={input}
                onChange={e => onInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ',' || e.key === ';') { e.preventDefault(); adicionar(input); } else if (e.key === 'Backspace' && !input && value.length) { onChange(value.slice(0, -1), ''); } }}
                onBlur={() => input.trim() && adicionar(input)}
                placeholder={value.length ? 'Adicionar outro...' : placeholder}
                className="flex-1 min-w-[180px] bg-transparent px-1 py-1 text-sm text-white focus:outline-none placeholder:text-slate-600"
            />
        </div>
    );
}

const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const toDate = (v) => {
    if (!v) return null;
    if (v.toDate) return v.toDate();
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
};
const fmtDate = (v) => { const d = toDate(v); return d ? d.toLocaleDateString('pt-BR') : '—'; };
const fmtDateTime = (v) => { const d = toDate(v); return d ? d.toLocaleString('pt-BR') : '—'; };
const ymdToBR = (s) => (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) ? s.split('-').reverse().join('/') : '—';
const todayYMD = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const normalize = (v) => String(v || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();

// Chamado entra no Financeiro quando tem valor e foi finalizado/faturado como cobrança avulsa
const isBillableTicket = (t) => {
    const amt = Number(t.totalAmount) || 0;
    if (amt <= 0) return false;
    const st = normalize(t.status);
    return t.faturamentoTipo === 'com_nota'
        || t.faturamentoTipo === 'sem_nota'
        || st === 'faturado'
        || st.includes('com cobranca')
        || st.includes('avulso');
};

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

export default function FinanceiroPage({ permissionLevel, allowedCompanies, uiItems = {}, navigateTo, canOpenTickets = false }) {
    const userEmail = auth.currentUser?.email || '';
    const isUserAdmin = userEmail === ADMIN_EMAIL;
    const canEdit = permissionLevel === 'edit';
    const isAllowed = (itemId) => isUserAdmin || uiItems[itemId] !== false;
    const canSync = canEdit && isAllowed('sync-tickets');
    const canChangeStage = canEdit && isAllowed('change-stage');
    const canCancel = canEdit && isAllowed('cancel-charge');
    const canEditDetails = canEdit && isAllowed('edit-details');
    const canFaturar = canEdit && isAllowed('faturar');
    const canSend = canEdit && isAllowed('send-charge');

    // --- Dados ---
    const [tickets, setTickets] = useState([]);
    const [ticketsLoaded, setTicketsLoaded] = useState(false);
    const [cobrancas, setCobrancas] = useState([]);
    const [cobrancasLoaded, setCobrancasLoaded] = useState(false);
    const [financError, setFinancError] = useState('');
    const [clientsContrato, setClientsContrato] = useState([]);
    const [clientsAvulsos, setClientsAvulsos] = useState([]);

    // --- UI ---
    const [etapaFilter, setEtapaFilter] = useState('a_faturar');
    const [search, setSearch] = useState('');
    const [companyFilter, setCompanyFilter] = useState('all');
    const [tipoFilter, setTipoFilter] = useState('all');
    const [onlyOverdue, setOnlyOverdue] = useState(false);
    const [periodAll, setPeriodAll] = useState(true);
    const [periodMonth, setPeriodMonth] = useState(new Date().getMonth());
    const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
    const [selected, setSelected] = useState(new Set());
    const [detail, setDetail] = useState(null); // id da cobrança aberta
    const [detailForm, setDetailForm] = useState({ vencimento: '', observacoes: '' });
    const [syncing, setSyncing] = useState(false);
    const [lastSync, setLastSync] = useState(null);
    const [busy, setBusy] = useState(false);
    const [toast, setToast] = useState(null); // { msg, type: 'ok' | 'error' }
    const [confirmDlg, setConfirmDlg] = useState(null); // { ids, etapa, total, motivo, dataPagamento, valorPago, forcarVhsys }
    const [vhsysSyncing, setVhsysSyncing] = useState(false);
    const [fatDlg, setFatDlg] = useState(null); // { ids, modo: 'faturar'|'agrupar', comNota, vencimento, idBanco, formaPagamento, obs }
    const [desfazerDlg, setDesfazerDlg] = useState(null); // { id, numeros }
    const [envioDlg, setEnvioDlg] = useState(null); // { id, para, paraInput, cc, ccInput, assunto, mensagem, anexos, salvar, ... }
    const [envioFase, setEnvioFase] = useState(''); // texto de progresso do envio
    const [contasVhsys, setContasVhsys] = useState(null); // [{ id, nome }]
    const [formasPagamento, setFormasPagamento] = useState(FORMAS_PAGAMENTO_PADRAO);
    const [contasErro, setContasErro] = useState('');
    const carregarContasVhsys = async () => {
        if (contasVhsys) return;
        try {
            const res = await callFn('financeiroListarContasVhsys', {});
            setContasVhsys(res?.contas || []);
            if (Array.isArray(res?.formas) && res.formas.length) setFormasPagamento(res.formas);
            setContasErro('');
        } catch (err) {
            setContasVhsys([]);
            setContasErro(err?.code === 'functions/not-found'
                ? 'Lista de contas indisponível (falta publicar a função financeiroListarContasVhsys).'
                : 'Não foi possível carregar as contas do VHSYS.');
        }
    };
    const autoSyncDone = useRef(false);
    const allCompaniesRef = useRef([]);

    // --- Envio de cobrança por e-mail ---
    const envioInfo = (c) => {
        const etapa = c.etapa || 'a_cobrar';
        const comNota = c.faturamentoTipo === 'com_nota' || !!(c.vhsysNfseId || c.vhsysRpsNumero || c.nfseChave);
        const forma = c.vhsysFormaPagamento || c.formaPagamento || '';
        let motivo = '';
        if (!['a_cobrar', 'cobrado'].includes(etapa)) motivo = 'Só é possível enviar cobranças em A Cobrar ou Cobrança Enviada.';
        else if (!c.vhsysLinkBoleto && !usaBoleto(forma) && !comNota && !c.vhsysReceitaId) motivo = 'A conta a receber ainda não existe no VHSYS.';
        else if (!c.vhsysLinkBoleto && usaBoleto(forma)) motivo = comNota ? 'O boleto ainda não existe: emita a NF, lance as contas no VHSYS e sincronize.' : 'O boleto ainda não existe no VHSYS: sincronize com o VHSYS.';
        else if (comNota && !c.nfseChave) motivo = 'O XML da NF ainda não foi obtido: use "Buscar NF" nos documentos fiscais.';
        return { comNota, motivo, pode: !motivo, temBoleto: !!c.vhsysLinkBoleto, forma };
    };
    const empresaDaCobranca = (c) => allCompaniesRef.current.find(x => x.id === c.empresaId)
        || allCompaniesRef.current.find(x => (x.nomeFantasia || x.name) === c.empresaNome) || null;

    const abrirEnvio = (c) => {
        const info = envioInfo(c);
        if (!info.pode) { showToast(info.motivo, 'error'); return; }
        const emp = empresaDaCobranca(c);
        const cadastrados = Array.isArray(emp?.emailsFaturamento) ? emp.emailsFaturamento.filter(e => EMAIL_RE.test(String(e))) : [];
        const emailCadastro = EMAIL_RE.test(String(emp?.email || '').trim()) ? String(emp.email).trim().toLowerCase() : '';
        const numeros = (c.faturaNumeros && c.faturaNumeros.length) ? c.faturaNumeros : [`A${c.numeroSequencial}`];
        setEnvioDlg({
            id: c.id,
            numeros,
            empresaNome: c.empresaNome || '',
            empresaId: emp?.id || c.empresaId || '',
            comNota: info.comNota,
            temBoleto: info.temBoleto,
            forma: info.forma,
            reenvio: (c.etapa || 'a_cobrar') === 'cobrado' || (c.envios || []).length > 0,
            cadastrados,
            emailCadastro,
            para: [...cadastrados],
            paraInput: '',
            paraErro: '',
            cc: [],
            ccInput: '',
            ccErro: '',
            assunto: `Arcus Informática — Cobrança${c.nfseNumero ? ` NF ${c.nfseNumero}` : ''} — ${numeros.join(', ')}`,
            mensagem: '',
            anexos: { boleto: info.temBoleto, danfse: info.comNota, xml: info.comNota },
            salvar: cadastrados.length === 0
        });
    };

    const enviarCobranca = async () => {
        const dlg = envioDlg;
        if (!dlg) return;
        const c = cobrancas.find(x => x.id === dlg.id);
        if (!c) { showToast('Cobrança não encontrada.', 'error'); return; }
        // Inclui o que ficou digitado e não virou "chip"
        const pend = (lista, texto) => [...new Set([...lista, ...separarEmails(texto)])];
        const para = pend(dlg.para, dlg.paraInput);
        const cc = pend(dlg.cc, dlg.ccInput).filter(e => !para.includes(e));
        const invalidos = [...para, ...cc].filter(e => !EMAIL_RE.test(e));
        if (!para.length) { setEnvioDlg(d => ({ ...d, paraErro: 'Informe ao menos um e-mail de destino.' })); return; }
        if (invalidos.length) { setEnvioDlg(d => ({ ...d, paraErro: `E-mail inválido: ${invalidos.join(', ')}` })); return; }
        const novos = para.filter(e => !dlg.cadastrados.includes(e));

        setBusy(true);
        try {
            if (dlg.comNota && dlg.anexos.danfse) {
                setEnvioFase('Gerando o DANFSe...');
                const chave = c.nfseChave;
                const nf = await getDoc(doc(dbFinanc, 'nfse', chave));
                const xml = nf.exists() ? nf.data().xml : '';
                if (!xml) throw new Error('XML da NF não encontrado. Use "Buscar NF" nos documentos fiscais.');
                const blob = await gerarDanfsePdf(xml);
                setEnvioFase('Salvando o DANFSe...');
                await uploadBytes(storageRef(storage, `nfse/${chave}.pdf`), blob, { contentType: 'application/pdf' });
            }
            setEnvioFase('Enviando o e-mail...');
            const res = await callFn('financeiroEnviarCobranca', {
                cobrancaId: c.id,
                para,
                cc,
                assunto: dlg.assunto,
                mensagem: dlg.mensagem,
                anexos: dlg.anexos,
                salvarNoCadastro: dlg.salvar && novos.length > 0
            });
            setEnvioDlg(null);
            const extras = [];
            if (res?.boletoComoLink && dlg.anexos.boleto) extras.push('boleto enviado como link');
            if (res?.salvosNoCadastro) extras.push('e-mails salvos no cadastro');
            showToast(`Cobrança enviada para ${para.join(', ')}${extras.length ? ` · ${extras.join(' · ')}` : ''}`);
        } catch (err) {
            const msg = err?.code === 'functions/not-found' ? 'Função de envio ainda não publicada (financeiroEnviarCobranca).'
                : err?.code === 'storage/unauthorized' ? 'Sem permissão para salvar o DANFSe no Storage (pasta nfse/). Ajuste as regras do Storage.'
                : (err?.message || String(err));
            showToast(msg, 'error');
        } finally {
            setBusy(false);
            setEnvioFase('');
        }
    };

    const showToast = (msg, type = 'ok') => { setToast({ msg, type }); setTimeout(() => setToast(null), type === 'error' ? 6000 : 3500); };

    // --- Listeners ---
    useEffect(() => {
        const base = `/artifacts/${appId}/public/data`;
        const unsubTickets = onSnapshot(collection(db, `${base}/tickets`), snap => {
            setTickets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setTicketsLoaded(true);
        }, err => { console.error('Financeiro: erro ao ler chamados', err); setTicketsLoaded(true); });
        const unsubC1 = onSnapshot(collection(db, `${base}/clients`), snap => setClientsContrato(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubC2 = onSnapshot(collection(db, `${base}/clients-avulsos`), snap => setClientsAvulsos(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubFin = onSnapshot(collection(dbFinanc, COBRANCAS), snap => {
            setCobrancas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setCobrancasLoaded(true);
            setFinancError('');
        }, err => {
            console.error('Financeiro: erro ao ler banco financ', err);
            setFinancError(err?.code === 'permission-denied'
                ? 'Sem permissão para acessar o banco "financ". Verifique as regras de segurança desse banco no console do Firebase.'
                : `Erro ao acessar o banco "financ": ${err?.message || err}`);
            setCobrancasLoaded(true);
        });
        return () => { unsubTickets(); unsubC1(); unsubC2(); unsubFin(); };
    }, []);

    // --- Empresas ---
    const allCompanies = useMemo(() => [...clientsContrato, ...clientsAvulsos], [clientsContrato, clientsAvulsos]);
    allCompaniesRef.current = allCompanies;
    const resolveCompany = useMemo(() => {
        const index = new Map();
        allCompanies.forEach(c => {
            if (c.id && !index.has(c.id)) index.set(c.id, c);
            [c.name, c.nomeFantasia].forEach(n => { const k = normalize(n); if (k && !index.has(k)) index.set(k, c); });
        });
        return (t) => {
            for (const cand of [t.clientCompanyId, t.clientCompany, t.clientCompanyName]) {
                if (!cand) continue;
                const found = index.get(cand) || index.get(normalize(cand));
                if (found) return found;
            }
            return null;
        };
    }, [allCompanies]);

    // Monta os campos "espelho" do chamado que ficam gravados na cobrança
    const snapshotFromTicket = (t) => {
        const comp = resolveCompany(t);
        return {
            ticketId: t.id,
            numeroSequencial: t.numeroSequencial || 0,
            titulo: t.title || '',
            clienteNome: t.clientName || '',
            empresaId: comp?.id || t.clientCompanyId || '',
            empresaNome: comp ? (comp.nomeFantasia || comp.name || '') : (t.clientCompanyName || t.clientCompany || ''),
            empresaRazaoSocial: comp?.name || '',
            valor: Number(t.totalAmount) || 0,
            faturamentoTipo: t.faturamentoTipo || '',
            preferenciaNota: t.preferenciaNota || '',
            statusChamado: t.status || '',
            vhsysReceitaId: t.vhsysReceitaId || '',
            vhsysNfseId: t.vhsysNfseId || '',
            vhsysRpsNumero: t.vhsysRpsNumero || '',
            dataChamado: toDate(t.createdAt) || null
        };
    };
    const SNAPSHOT_KEYS = ['preferenciaNota', 'numeroSequencial', 'titulo', 'clienteNome', 'empresaId', 'empresaNome', 'empresaRazaoSocial', 'valor', 'faturamentoTipo', 'statusChamado', 'vhsysReceitaId', 'vhsysNfseId', 'vhsysRpsNumero'];

    // --- Sincronização chamados (default) → cobranças (financ) ---
    const runSync = async ({ silent = false } = {}) => {
        if (!canSync || financError) return;
        setSyncing(true);
        try {
            const existing = new Map(cobrancas.map(c => [c.id, c]));
            const ops = [];
            tickets.filter(isBillableTicket).forEach(t => {
                const snap = snapshotFromTicket(t);
                const current = existing.get(t.id);
                if (!current) {
                    const jaLancado = !!(t.vhsysReceitaId || t.vhsysNfseId || t.vhsysRpsNumero);
                    const etapaInicial = jaLancado ? 'a_cobrar' : 'a_faturar';
                    ops.push({ type: 'create', id: t.id, data: {
                        ...snap,
                        etapa: etapaInicial,
                        vencimento: '',
                        observacoes: '',
                        dataCobranca: null,
                        dataRecebimento: null,
                        dataCancelamento: null,
                        historico: [{ etapa: etapaInicial, em: new Date().toISOString(), por: userEmail || 'sistema', nota: jaLancado ? 'Importado do chamado' : 'Chamado finalizado com cobrança — aguardando faturamento' }],
                        criadoEm: serverTimestamp(),
                        atualizadoEm: serverTimestamp()
                    }});
                } else {
                    const changed = {};
                    SNAPSHOT_KEYS.forEach(k => {
                        // Não apaga vínculos com o VHSYS feitos pela sincronização ou manualmente
                        if (['vhsysReceitaId', 'vhsysNfseId', 'vhsysRpsNumero'].includes(k) && !snap[k]) return;
                        if ((current[k] ?? '') !== (snap[k] ?? '')) changed[k] = snap[k];
                    });
                    if (Object.keys(changed).length > 0) {
                        ops.push({ type: 'update', id: t.id, data: { ...changed, atualizadoEm: serverTimestamp() } });
                    }
                }
            });

            for (let i = 0; i < ops.length; i += 400) {
                const batch = writeBatch(dbFinanc);
                ops.slice(i, i + 400).forEach(op => {
                    const ref = doc(dbFinanc, COBRANCAS, op.id);
                    if (op.type === 'create') batch.set(ref, op.data);
                    else batch.update(ref, op.data);
                });
                await batch.commit();
            }
            setLastSync(new Date());
            const created = ops.filter(o => o.type === 'create').length;
            const updated = ops.length - created;
            if (!silent || created > 0) {
                showToast(ops.length === 0 ? 'Tudo sincronizado.' : `Sincronizado: ${created} nova(s), ${updated} atualizada(s).`);
            }
        } catch (err) {
            console.error('Financeiro: erro na sincronização', err);
            if (!silent) showToast('Erro ao sincronizar com o banco financ: ' + (err?.message || err), 'error');
        } finally {
            setSyncing(false);
        }
    };

    // Sincroniza automaticamente uma vez ao abrir a página
    useEffect(() => {
        if (autoSyncDone.current) return;
        if (!ticketsLoaded || !cobrancasLoaded || financError || !canSync) return;
        if (allCompanies.length === 0) return; // espera os cadastros para gravar o nome correto da empresa
        autoSyncDone.current = true;
        runSync({ silent: true });
    }, [ticketsLoaded, cobrancasLoaded, financError, canSync, allCompanies.length]);

    // --- Visibilidade por empresa (mesma regra das outras páginas) ---
    const visibleCobrancas = useMemo(() => {
        if (allowedCompanies === null || allowedCompanies === undefined) return cobrancas;
        return cobrancas.filter(c =>
            allowedCompanies.includes(c.empresaId) ||
            allowedCompanies.includes(c.empresaNome) ||
            (c.empresaRazaoSocial && allowedCompanies.includes(c.empresaRazaoSocial))
        );
    }, [cobrancas, allowedCompanies]);

    const ticketIds = useMemo(() => new Set(tickets.map(t => t.id)), [tickets]);
    const today = todayYMD();
    const isOverdue = (c) => ['a_cobrar', 'cobrado'].includes(c.etapa || 'a_cobrar') && c.vencimento && c.vencimento < today;
    const diasAFaturar = (c) => {
        const d = toDate(c.criadoEm) || toDate(c.dataChamado);
        return d ? Math.floor((Date.now() - d.getTime()) / 86400000) : 0;
    };
    const faturarAtrasado = (c) => (c.etapa || '') === 'a_faturar' && diasAFaturar(c) > DIAS_ALERTA_FATURAR;

    // Filtros (exceto etapa) — usados também nos cards de resumo
    const baseFiltered = useMemo(() => {
        const q = normalize(search);
        return visibleCobrancas.filter(c => {
            if (companyFilter !== 'all' && c.empresaNome !== companyFilter) return false;
            if (tipoFilter !== 'all' && (c.faturamentoTipo || 'outros') !== tipoFilter) return false;
            if (!periodAll) {
                const d = toDate(c.dataChamado);
                if (!d || d.getMonth() !== periodMonth || d.getFullYear() !== periodYear) return false;
            }
            if (q) {
                const hay = normalize(`a${c.numeroSequencial} ${c.numeroSequencial} ${c.titulo} ${c.empresaNome} ${c.empresaRazaoSocial} ${c.clienteNome} ${c.vhsysReceitaId} ${c.vhsysRpsNumero}`);
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [visibleCobrancas, search, companyFilter, tipoFilter, periodAll, periodMonth, periodYear]);

    const summary = useMemo(() => {
        const s = {};
        ETAPA_ORDER.forEach(e => { s[e] = { count: 0, sum: 0, overdueCount: 0, overdueSum: 0 }; });
        let overdueCount = 0, overdueSum = 0, faturarAtrasadas = 0;
        baseFiltered.forEach(c => {
            const e = s[c.etapa] ? c.etapa : 'a_cobrar';
            s[e].count++; s[e].sum += Number(c.valor) || 0;
            if (isOverdue(c)) { overdueCount++; overdueSum += Number(c.valor) || 0; s[e].overdueCount++; s[e].overdueSum += Number(c.valor) || 0; }
            if (faturarAtrasado(c)) faturarAtrasadas++;
        });
        return { ...s, overdueCount, overdueSum, faturarAtrasadas };
    }, [baseFiltered, today]);

    const list = useMemo(() => {
        let l = baseFiltered;
        if (etapaFilter !== 'all') l = l.filter(c => (c.etapa || 'a_cobrar') === etapaFilter);
        if (onlyOverdue) l = l.filter(isOverdue);
        return [...l].sort((a, b) => (b.numeroSequencial || 0) - (a.numeroSequencial || 0));
    }, [baseFiltered, etapaFilter, onlyOverdue, today]);

    const companyOptions = useMemo(() => [...new Set(visibleCobrancas.map(c => c.empresaNome).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [visibleCobrancas]);
    const yearOptions = useMemo(() => {
        const ys = new Set([new Date().getFullYear()]);
        visibleCobrancas.forEach(c => { const d = toDate(c.dataChamado); if (d) ys.add(d.getFullYear()); });
        return [...ys].sort((a, b) => b - a);
    }, [visibleCobrancas]);

    // Limpa seleção ao trocar filtros
    useEffect(() => { setSelected(new Set()); }, [etapaFilter, search, companyFilter, tipoFilter, onlyOverdue, periodAll, periodMonth, periodYear]);

    const pendingImport = useMemo(() => {
        const ids = new Set(cobrancas.map(c => c.id));
        return tickets.filter(t => isBillableTicket(t) && !ids.has(t.id)).length;
    }, [tickets, cobrancas]);

    // --- Mudança de etapa (reflete no VHSYS quando há conta a receber vinculada) ---
    // Recebido → liquida no VHSYS · sair de Recebido → desliquida · Cancelado → lixeira do VHSYS
    // A Cobrar ↔ Cobrado é controle interno (não mexe no VHSYS)
    const acaoVhsys = (current, novaEtapa) => {
        const de = current.etapa || 'a_cobrar';
        if (novaEtapa === 'recebido') return 'liquidar';
        if (novaEtapa === 'cancelado') return 'excluir';
        if (de === 'recebido') return 'desliquidar';
        return null;
    };

    const changeEtapaLocal = async (items, novaEtapa, nota, extra = {}) => {
        const nowIso = new Date().toISOString();
        for (let i = 0; i < items.length; i += 400) {
            const batch = writeBatch(dbFinanc);
            items.slice(i, i + 400).forEach(current => {
                if (current.etapa === novaEtapa) return;
                const data = {
                    etapa: novaEtapa,
                    atualizadoEm: serverTimestamp(),
                    historico: arrayUnion({ etapa: novaEtapa, de: current.etapa || 'a_cobrar', em: nowIso, por: userEmail || 'desconhecido', nota: nota || '' })
                };
                const dateField = DATE_FIELD_BY_ETAPA[novaEtapa];
                if (dateField) data[dateField] = serverTimestamp();
                if (novaEtapa === 'recebido' && extra.dataPagamento) data.dataRecebimento = new Date(`${extra.dataPagamento}T12:00:00`);
                if (novaEtapa === 'recebido') {
                    data.contaBancariaId = extra.idBanco || '';
                    data.contaBancariaNome = extra.nomeBanco || '';
                    data.formaPagamento = extra.formaPagamento || '';
                    data.obsPagamento = extra.obsPagamento || '';
                }
                if (novaEtapa === 'a_cobrar') { data.dataCobranca = null; data.dataRecebimento = null; data.dataCancelamento = null; }
                if (novaEtapa === 'cobrado') { data.dataRecebimento = null; data.dataCancelamento = null; }
                batch.update(doc(dbFinanc, COBRANCAS, current.id), data);
            });
            await batch.commit();
        }
    };

    const changeEtapa = async (ids, novaEtapa, nota = '', extra = {}) => {
        if (!ids.length) return;
        if (novaEtapa === 'cancelado' ? !canCancel : !canChangeStage) return;
        let items = ids.map(id => cobrancas.find(c => c.id === id)).filter(Boolean);
        const naoFaturadas = items.filter(c => (c.etapa || '') === 'a_faturar' && novaEtapa !== 'cancelado');
        if (naoFaturadas.length) {
            showToast(`${naoFaturadas.length} cobrança(s) ainda não faturada(s): use "Faturar" primeiro.`, 'error');
            items = items.filter(c => !naoFaturadas.includes(c));
            if (!items.length) return;
        }

        // Cancelada com conta no VHSYS (na lixeira de lá) não pode ser reativada pela Intranet
        const bloqueadas = items.filter(c => c.etapa === 'cancelado' && novaEtapa !== 'cancelado' && c.vhsysReceitaId);
        const validos = items.filter(c => !bloqueadas.includes(c) && (c.etapa !== novaEtapa || extra.forcarVhsys));
        if (!validos.length) {
            if (bloqueadas.length) showToast('Cobrança cancelada com conta no VHSYS: restaure a conta na lixeira do VHSYS e sincronize.', 'error');
            return;
        }

        setBusy(true);
        const falhas = [];
        try {
            const porAcao = {};
            const locais = [];
            validos.forEach(c => {
                const acao = acaoVhsys(c, novaEtapa);
                if (acao) (porAcao[acao] = porAcao[acao] || []).push(c);
                else locais.push(c);
            });

            for (const [acao, lista] of Object.entries(porAcao)) {
                try {
                    const res = await callFn('financeiroAcaoVhsys', {
                        ids: lista.map(c => c.id),
                        acao,
                        nota,
                        etapaDestino: novaEtapa,
                        dataPagamento: extra.dataPagamento || '',
                        valorPago: lista.length === 1 ? (extra.valorPago ?? '') : '',
                        idBanco: extra.idBanco || '',
                        nomeBanco: extra.nomeBanco || '',
                        formaPagamento: extra.formaPagamento || '',
                        obsPagamento: extra.obsPagamento || ''
                    });
                    (res?.resultados || []).filter(r => !r.ok).forEach(r => falhas.push(r));
                } catch (err) {
                    // Função ainda não publicada: mantém o comportamento anterior (só na Intranet)
                    if (err?.code === 'functions/not-found') locais.push(...lista);
                    else throw err;
                }
            }
            if (locais.length) await changeEtapaLocal(locais, novaEtapa, nota, extra);

            setSelected(new Set());
            const okCount = validos.length - falhas.length;
            if (falhas.length) {
                showToast(`${okCount} ok · ${falhas.length} falharam no VHSYS: ${falhas[0].erro}`, 'error');
            } else {
                showToast(`${okCount} cobrança(s) → ${ETAPAS[novaEtapa].label}${bloqueadas.length ? ` · ${bloqueadas.length} bloqueada(s)` : ''}`);
            }
        } catch (err) {
            console.error('Financeiro: erro ao alterar etapa', err);
            showToast('Erro ao alterar etapa: ' + (err?.message || err), 'error');
        } finally {
            setBusy(false);
        }
    };

    // --- Faturamento (1 ou vários chamados → 1 lançamento no VHSYS) ---
    const addDias = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
    const abrirFaturamento = (ids, modo = 'faturar') => {
        const items = ids.map(id => cobrancas.find(c => c.id === id)).filter(Boolean);
        if (!items.length) return;
        const empresas = new Set(items.map(c => c.empresaId || ''));
        if (empresas.size !== 1 || empresas.has('')) { showToast('Selecione cobranças de um mesmo cliente.', 'error'); return; }
        if (modo === 'faturar' && items.some(c => (c.etapa || '') !== 'a_faturar' || c.faturaId)) { showToast('Só é possível faturar cobranças em "A Faturar".', 'error'); return; }
        if (modo === 'agrupar' && items.some(c => c.faturaId || !['a_cobrar', 'cobrado', 'a_faturar'].includes(c.etapa || 'a_cobrar'))) { showToast('Só dá para agrupar cobranças ainda não pagas e fora de outro faturamento.', 'error'); return; }
        const prefs = new Set(items.map(c => c.preferenciaNota || ''));
        const prefUnica = prefs.size === 1 ? [...prefs][0] : '';
        setFatDlg({
            ids: items.map(c => c.id),
            modo,
            comNota: prefUnica === 'com_nota' ? true : (prefUnica === 'sem_nota' ? false : null),
            vencimento: addDias(7),
            idBanco: (() => { try { return localStorage.getItem(PREF_CONTA) || ''; } catch (e) { return ''; } })(),
            formaPagamento: (() => { try { return localStorage.getItem(PREF_FORMA) || ''; } catch (e) { return ''; } })(),
            obs: ''
        });
        carregarContasVhsys();
    };
    const enviarFaturamento = async () => {
        if (!fatDlg) return;
        const { ids, modo, comNota, vencimento, idBanco, formaPagamento, obs } = fatDlg;
        if (comNota === null || !vencimento || !idBanco || !formaPagamento) return;
        try { localStorage.setItem(PREF_CONTA, idBanco); localStorage.setItem(PREF_FORMA, formaPagamento); } catch (e) {}
        const nomeBanco = (contasVhsys || []).find(b => b.id === idBanco)?.nome || '';
        setBusy(true);
        try {
            const res = await callFn(modo === 'agrupar' ? 'financeiroAgrupar' : 'financeiroFaturar', { ids, comNota, vencimento, idBanco, nomeBanco, formaPagamento, obs });
            if (res?.success) {
                setFatDlg(null);
                setSelected(new Set());
                showToast(`${modo === 'agrupar' ? 'Agrupado' : 'Faturado'}: ${(res.numeros || []).join(', ')} · ${fmtBRL(res.total)} · ${comNota ? `NFS-e RPS ${res.vhsysRpsNumero || res.vhsysNfseId}` : `Receita ${res.vhsysReceitaId}`}`);
            } else {
                showToast(res?.motivo || 'Não foi possível concluir.', 'error');
                if (modo === 'agrupar') setFatDlg(null);
            }
        } catch (err) {
            showToast(err?.code === 'functions/not-found' ? 'Função de faturamento ainda não publicada.' : (err?.message || String(err)), 'error');
        } finally {
            setBusy(false);
        }
    };
    const desfazerFaturamento = async () => {
        if (!desfazerDlg) return;
        setBusy(true);
        try {
            const res = await callFn('financeiroDesfazerFatura', { cobrancaId: desfazerDlg.id });
            if (res?.success) { setDesfazerDlg(null); setDetail(null); showToast('Faturamento desfeito: as cobranças voltaram para "A Faturar".'); }
            else showToast(res?.motivo || 'Não foi possível desfazer.', 'error');
        } catch (err) {
            showToast(err?.message || String(err), 'error');
        } finally {
            setBusy(false);
        }
    };

    // --- Sincronização com o VHSYS (Cloud Function) ---
    const runVhsysSync = async (ids = null) => {
        setVhsysSyncing(true);
        try {
            const res = await callFn('financeiroSincronizarVhsys', ids ? { ids } : {});
            const r = res?.resumo || {};
            const partes = [`${r.verificadas || 0} verificada(s)`];
            if (r.recebidas) partes.push(`${r.recebidas} recebida(s)`);
            if (r.canceladas) partes.push(`${r.canceladas} cancelada(s)`);
            if (r.desliquidadas) partes.push(`${r.desliquidadas} desliquidada(s)`);
            if (r.atrasadas) partes.push(`${r.atrasadas} vencida(s) → Cobrado`);
            if (r.vinculadas) partes.push(`${r.vinculadas} conta(s) de NF vinculada(s)`);
            if (r.erros) partes.push(`${r.erros} erro(s)`);
            showToast(`VHSYS: ${partes.join(' · ')}`, r.erros ? 'error' : 'ok');
        } catch (err) {
            showToast(err?.code === 'functions/not-found'
                ? 'A sincronização com o VHSYS ainda não foi publicada (falta o deploy das functions).'
                : 'Erro ao sincronizar com o VHSYS: ' + (err?.message || err), 'error');
        } finally {
            setVhsysSyncing(false);
        }
    };

    const confirmAndChange = (ids, novaEtapa, opts = {}) => {
        const total = ids.reduce((acc, id) => acc + (Number(cobrancas.find(c => c.id === id)?.valor) || 0), 0);
        // Recebido (data/valor), cancelamento (motivo) e alterações em massa pedem confirmação
        if (novaEtapa === 'cancelado' || novaEtapa === 'recebido' || ids.length > 1 || opts.forcarVhsys) {
            const single = ids.length === 1 ? cobrancas.find(c => c.id === ids[0]) : null;
            setConfirmDlg({
                ids, etapa: novaEtapa, total, motivo: '',
                dataPagamento: today,
                valorPago: single ? String(Number(single.valor) || 0) : '',
                idBanco: (() => { try { return localStorage.getItem(PREF_CONTA) || ''; } catch (e) { return ''; } })(),
                formaPagamento: (() => { try { return localStorage.getItem(PREF_FORMA) || ''; } catch (e) { return ''; } })(),
                obsPagamento: '',
                forcarVhsys: !!opts.forcarVhsys
            });
            if (novaEtapa === 'recebido') carregarContasVhsys();
            return;
        }
        changeEtapa(ids, novaEtapa, '');
    };

    const submitConfirm = async () => {
        if (!confirmDlg) return;
        if (confirmDlg.etapa === 'cancelado' && !confirmDlg.motivo.trim()) return;
        const { ids, etapa, motivo, dataPagamento, valorPago, forcarVhsys, idBanco, formaPagamento, obsPagamento } = confirmDlg;
        if (etapa === 'recebido') {
            if (!idBanco || !formaPagamento) return;
            try { localStorage.setItem(PREF_CONTA, idBanco); localStorage.setItem(PREF_FORMA, formaPagamento); } catch (e) {}
        }
        const nomeBanco = (contasVhsys || []).find(b => b.id === idBanco)?.nome || '';
        setConfirmDlg(null);
        await changeEtapa(ids, etapa, motivo.trim(), { dataPagamento, valorPago, forcarVhsys, idBanco, nomeBanco, formaPagamento, obsPagamento });
    };

    // --- Detalhe ---
    const detailItem = detail ? cobrancas.find(c => c.id === detail) : null;

    // Atalho: abre o chamado completo na página de Chamados
    const openTicket = (cobranca) => {
        if (!navigateTo || !cobranca?.ticketId) return;
        try { sessionStorage.setItem('arcus_open_ticket_id', cobranca.ticketId); } catch (e) {}
        setDetail(null);
        navigateTo('tickets');
    };

    // Ao voltar do chamado, reabre a cobrança de onde o usuário saiu
    const pendingCobrancaRef = useRef((() => {
        try {
            const id = sessionStorage.getItem('arcus_open_cobranca_id');
            if (id) sessionStorage.removeItem('arcus_open_cobranca_id');
            return id;
        } catch (e) { return null; }
    })());
    useEffect(() => {
        const id = pendingCobrancaRef.current;
        if (!id || !cobrancasLoaded) return;
        pendingCobrancaRef.current = null;
        const c = cobrancas.find(x => x.id === id);
        if (c) {
            setEtapaFilter('all');
            setDetail(c.id);
            setDetailForm({ vencimento: c.vencimento || '', observacoes: c.observacoes || '', vhsysReceitaId: c.vhsysReceitaId || '' });
        }
    }, [cobrancasLoaded, cobrancas]);
    const openDetail = (c) => {
        setDetail(c.id);
        setDetailForm({ vencimento: c.vencimento || '', observacoes: c.observacoes || '', vhsysReceitaId: c.vhsysReceitaId || '' });
    };
    const saveDetail = async () => {
        if (!detailItem || !canEditDetails) return;
        setBusy(true);
        try {
            const novoVinculo = String(detailForm.vhsysReceitaId || '').trim();
            const vinculoMudou = novoVinculo !== String(detailItem.vhsysReceitaId || '');
            const vencMudou = (detailForm.vencimento || '') !== (detailItem.vencimento || '');
            const data = { observacoes: detailForm.observacoes || '', atualizadoEm: serverTimestamp() };
            if (vinculoMudou) {
                if (novoVinculo && !/^\d+$/.test(novoVinculo)) { showToast('O ID da conta a receber deve conter só números.', 'error'); return; }
                data.vhsysReceitaId = novoVinculo;
                data.vhsysVinculoAuto = false;
                data.vhsysSituacao = '';
                data.vhsysDivergencia = '';
            }
            // Vencimento: com conta no VHSYS, altera lá primeiro; sem conta, só na Intranet
            const receitaFinal = vinculoMudou ? novoVinculo : detailItem.vhsysReceitaId;
            if (vencMudou && (!receitaFinal || !detailForm.vencimento)) data.vencimento = detailForm.vencimento || '';
            await updateDoc(doc(dbFinanc, COBRANCAS, detailItem.id), data);

            if (vencMudou && receitaFinal && detailForm.vencimento) {
                try {
                    const res = await callFn('financeiroAcaoVhsys', { ids: [detailItem.id], acao: 'vencimento', vencimento: detailForm.vencimento });
                    const r = res?.resultados?.[0];
                    if (r && !r.ok) { showToast(`Detalhes salvos, mas o vencimento não mudou no VHSYS: ${r.erro}`, 'error'); return; }
                } catch (err) {
                    if (err?.code === 'functions/not-found') {
                        await updateDoc(doc(dbFinanc, COBRANCAS, detailItem.id), { vencimento: detailForm.vencimento });
                    } else { showToast('Detalhes salvos, mas erro ao alterar vencimento no VHSYS: ' + (err?.message || err), 'error'); return; }
                }
            }
            if (vinculoMudou && novoVinculo) await runVhsysSync([detailItem.id]);
            else showToast('Detalhes salvos.');
        } catch (err) {
            showToast('Erro ao salvar: ' + (err?.message || err), 'error');
        } finally {
            setBusy(false);
        }
    };

    const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const allSelected = list.length > 0 && list.every(c => selected.has(c.id));
    const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(list.map(c => c.id)));
    const selectedIds = [...selected];
    const selectedSum = selectedIds.reduce((acc, id) => acc + (Number(cobrancas.find(c => c.id === id)?.valor) || 0), 0);

    // --- Impressão ---
    const handlePrint = () => {
        const items = selectedIds.length > 0 ? list.filter(c => selected.has(c.id)) : list;
        const parts = [];
        if (selectedIds.length > 0) parts.push(`${selectedIds.length} cobrança(s) selecionada(s)`);
        parts.push(`Etapa: ${etapaFilter === 'all' ? 'Todas' : ETAPAS[etapaFilter].label}${onlyOverdue ? ' (somente vencidas)' : ''}`);
        parts.push(`Empresa: ${companyFilter === 'all' ? 'Todas' : companyFilter}`);
        parts.push(`Tipo: ${tipoFilter === 'all' ? 'Com e sem NF' : (tipoFilter === 'outros' ? 'Sem tipo' : TIPO_LABEL[tipoFilter])}`);
        parts.push(`Período: ${periodAll ? 'Todo' : `${MONTHS[periodMonth]}/${periodYear}`}`);
        if (search) parts.push(`Busca: "${search}"`);
        const ok = printFinanceReport(items, { filtersText: parts.join(' · '), emittedBy: userEmail });
        if (!ok) showToast('O navegador bloqueou a janela de impressão. Libere pop-ups para este site.', 'error');
    };

    const EtapaBadge = ({ etapa }) => {
        const e = ETAPAS[etapa] || ETAPAS.a_cobrar;
        return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${e.color} ${e.bg} ${e.border} whitespace-nowrap`}>{e.label}</span>;
    };

    const vhsysRef = (c) => {
        if ((c.etapa || '') === 'a_faturar') return 'Ainda não faturado';
        const partes = [];
        if (c.vhsysRpsNumero || c.vhsysNfseId) partes.push(`NFS-e RPS ${c.vhsysRpsNumero || c.vhsysNfseId}${c.vhsysNotaNumero ? ` · NF ${c.vhsysNotaNumero}` : ''}`);
        if (c.vhsysReceitaId) partes.push(`Receita ${c.vhsysReceitaId}`);
        else if (c.vhsysNfseId || c.vhsysRpsNumero) partes.push(NOTA_STATUS[c.vhsysNotaStatus] || 'Conta a receber ainda não vinculada');
        return partes.length ? partes.join(' · ') : 'Não lançado no VHSYS';
    };

    // Situação no VHSYS (o "Atrasado" é calculado na hora, pelo vencimento)
    const vhsysSit = (c) => {
        let sit = c.vhsysSituacao || '';
        if (sit === 'aberto' && c.vhsysVencimento && c.vhsysVencimento < today) sit = 'atrasado';
        if (sit === 'atrasado' && c.vhsysVencimento && c.vhsysVencimento >= today) sit = 'aberto';
        return sit;
    };
    const VhsysDot = ({ c, withLabel = false }) => {
        const sit = vhsysSit(c);
        const cfg = VHSYS_SIT[sit];
        const title = cfg ? `VHSYS: ${cfg.label}` : (c.vhsysReceitaId ? 'VHSYS: ainda não sincronizado' : 'Sem conta a receber no VHSYS');
        return (
            <span className="inline-flex items-center gap-1.5" title={title}>
                {cfg
                    ? <span className={`w-3 h-3 rounded-full flex-shrink-0 ${cfg.dot}`} />
                    : <span className="w-3 h-3 rounded-full flex-shrink-0 border-2 border-dashed border-slate-600" />}
                {withLabel && <span className="text-sm">{cfg ? cfg.label : (c.vhsysReceitaId ? 'Não sincronizado' : 'Sem conta')}</span>}
            </span>
        );
    };

    const loading = !ticketsLoaded || !cobrancasLoaded;

    // ======================= RENDER =======================
    return (
        <div className="p-4 sm:p-6 md:p-8 space-y-6 font-sans text-slate-200">
            {/* Cabeçalho */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cyan-500">Financeiro</p>
                    <h1 className="text-3xl font-black text-white tracking-tight">Cobranças</h1>
                    <p className="text-sm text-slate-400 mt-1">Chamados finalizados com valor a cobrar — controle de cobrança e recebimento.</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handlePrint}
                        disabled={loading}
                        className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-xl transition-colors disabled:opacity-50"
                        title={selectedIds.length > 0 ? 'Imprime as cobranças selecionadas' : 'Imprime as cobranças do filtro atual'}
                    >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                        {selectedIds.length > 0 ? `Imprimir (${selectedIds.length})` : 'Imprimir relatório'}
                    </button>
                    {canSync && (
                        <button
                            onClick={() => runVhsysSync()}
                            disabled={vhsysSyncing || loading || !!financError}
                            className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-xl transition-colors disabled:opacity-50"
                            title="Atualiza a situação das cobranças com o VHSYS (liquidadas, canceladas, vencimentos e contas das notas)"
                        >
                            <svg className={`h-4 w-4 ${vhsysSyncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                            {vhsysSyncing ? 'Consultando VHSYS...' : 'Sincronizar com VHSYS'}
                        </button>
                    )}
                    {lastSync && <span className="text-[11px] text-slate-500">Sincronizado às {lastSync.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>}
                    {canSync && (
                        <button
                            onClick={() => runSync()}
                            disabled={syncing || loading || !!financError}
                            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-xl transition-colors disabled:opacity-50"
                            title="Busca chamados com valor que ainda não estão no Financeiro e atualiza os dados dos existentes"
                        >
                            <svg className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                            {syncing ? 'Sincronizando...' : 'Sincronizar chamados'}
                        </button>
                    )}
                </div>
            </div>

            {financError && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-xl p-4">{financError}</div>
            )}
            {!financError && !loading && pendingImport > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm rounded-xl p-4 flex items-center justify-between gap-4">
                    <span>{pendingImport} chamado(s) com valor ainda não estão no Financeiro.</span>
                    {canSync && <button onClick={() => runSync()} disabled={syncing} className="text-xs font-bold uppercase tracking-wider text-amber-300 hover:text-amber-100 whitespace-nowrap">Importar agora</button>}
                </div>
            )}

            {/* Cards de resumo (clicáveis) */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                {ETAPA_ORDER.map(e => {
                    const cfg = ETAPAS[e];
                    const active = etapaFilter === e && !onlyOverdue;
                    return (
                        <button
                            key={e}
                            onClick={() => { setEtapaFilter(e); setOnlyOverdue(false); }}
                            className={`text-left bg-slate-800/60 backdrop-blur-xl p-4 rounded-2xl border transition-all ${active ? `${cfg.border} ring-1 ring-current ${cfg.color}` : 'border-white/10 hover:border-white/20'}`}
                        >
                            <span className={`text-[10px] uppercase tracking-wider font-black block ${cfg.color}`}>{cfg.label}</span>
                            <span className="text-2xl font-black font-mono text-white mt-1 block">{fmtBRL(summary[e].sum)}</span>
                            <span className="text-[11px] text-slate-400">{summary[e].count} cobrança(s)</span>
                            {e === 'a_faturar' && summary.faturarAtrasadas > 0 && (
                                <span className="block mt-2 text-[11px] font-bold text-red-400">{summary.faturarAtrasadas} há mais de {DIAS_ALERTA_FATURAR} dias</span>
                            )}
                            {(e === 'a_cobrar' || e === 'cobrado') && summary[e].overdueCount > 0 && (
                                <span
                                    role="button"
                                    onClick={(ev) => { ev.stopPropagation(); setEtapaFilter(e); setOnlyOverdue(true); }}
                                    className={`block mt-2 text-[11px] font-bold ${onlyOverdue && etapaFilter === e ? 'text-red-300 underline' : 'text-red-400 hover:underline'}`}
                                >
                                    {summary[e].overdueCount} vencida(s) · {fmtBRL(summary[e].overdueSum)}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Filtros */}
            <div className="bg-slate-800/60 backdrop-blur-xl border border-white/10 rounded-2xl p-4 flex flex-col lg:flex-row gap-3 lg:items-center">
                <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Buscar por nº, título, empresa, VHSYS..."
                    className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500"
                />
                <select value={etapaFilter} onChange={e => { setEtapaFilter(e.target.value); setOnlyOverdue(false); }} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none">
                    <option value="all">Todas as etapas</option>
                    {ETAPA_ORDER.map(e => <option key={e} value={e}>{ETAPAS[e].label}</option>)}
                </select>
                <select value={companyFilter} onChange={e => setCompanyFilter(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none max-w-[220px]">
                    <option value="all">Todas as empresas</option>
                    {companyOptions.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <select value={tipoFilter} onChange={e => setTipoFilter(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none">
                    <option value="all">Com e sem NF</option>
                    <option value="com_nota">Com NF</option>
                    <option value="sem_nota">Sem NF</option>
                    <option value="outros">Sem tipo (antigos)</option>
                </select>
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-slate-400 whitespace-nowrap">
                        <input type="checkbox" checked={periodAll} onChange={e => setPeriodAll(e.target.checked)} className="rounded border-slate-600 bg-slate-800 text-cyan-500" />
                        Todo período
                    </label>
                    {!periodAll && (
                        <>
                            <select value={periodMonth} onChange={e => setPeriodMonth(Number(e.target.value))} className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-sm text-white outline-none">
                                {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                            </select>
                            <select value={periodYear} onChange={e => setPeriodYear(Number(e.target.value))} className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-sm text-white outline-none">
                                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </>
                    )}
                </div>
            </div>

            {/* Barra de ações em massa */}
            {selectedIds.length > 0 && (canChangeStage || canCancel || canFaturar) && (
                <div className="sticky top-2 z-20 bg-slate-900/95 backdrop-blur border border-cyan-500/30 rounded-2xl p-3 flex flex-wrap items-center gap-2 shadow-2xl">
                    <span className="text-sm text-slate-300 mr-2">
                        <b className="text-white">{selectedIds.length}</b> selecionada(s) · <span className="font-mono text-emerald-400 font-bold">{fmtBRL(selectedSum)}</span>
                    </span>
                    {canFaturar && (() => {
                        const sel = selectedIds.map(id => cobrancas.find(c => c.id === id)).filter(Boolean);
                        const mesmoCliente = new Set(sel.map(c => c.empresaId || '')).size === 1 && sel[0]?.empresaId;
                        const todasAFaturar = sel.length > 0 && sel.every(c => (c.etapa || '') === 'a_faturar' && !c.faturaId);
                        const legadas = sel.length > 1 && sel.every(c => !c.faturaId && ['a_cobrar', 'cobrado'].includes(c.etapa || 'a_cobrar'));
                        return (
                            <>
                                {todasAFaturar && (
                                    <button disabled={busy || !mesmoCliente} onClick={() => abrirFaturamento(selectedIds, 'faturar')} title={mesmoCliente ? 'Gerar um único lançamento no VHSYS' : 'Selecione cobranças de um mesmo cliente'} className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border border-violet-500/40 bg-violet-500/15 text-violet-200 hover:brightness-125 disabled:opacity-40">
                                        Faturar ({selectedIds.length})
                                    </button>
                                )}
                                {legadas && (
                                    <button disabled={busy || !mesmoCliente} onClick={() => abrirFaturamento(selectedIds, 'agrupar')} title={mesmoCliente ? 'Substituir os lançamentos individuais por um único' : 'Selecione cobranças de um mesmo cliente'} className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border border-violet-500/40 bg-violet-500/15 text-violet-200 hover:brightness-125 disabled:opacity-40">
                                        Agrupar ({selectedIds.length})
                                    </button>
                                )}
                            </>
                        );
                    })()}
                    {canChangeStage && ['a_cobrar', 'cobrado', 'recebido'].map(e => (
                        <button key={e} disabled={busy} onClick={() => confirmAndChange(selectedIds, e)} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border ${ETAPAS[e].color} ${ETAPAS[e].bg} ${ETAPAS[e].border} hover:brightness-125 disabled:opacity-50`}>
                            → {ETAPAS[e].label}
                        </button>
                    ))}
                    {canCancel && (
                        <button disabled={busy} onClick={() => confirmAndChange(selectedIds, 'cancelado')} className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 hover:brightness-125 disabled:opacity-50">
                            Cancelar
                        </button>
                    )}
                    <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-slate-400 hover:text-white">Limpar seleção</button>
                </div>
            )}

            {/* Lista */}
            <div className="bg-slate-800/60 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    {(canChangeStage || canCancel) && (
                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="rounded border-slate-600 bg-slate-800 text-cyan-500" title="Selecionar todas da lista" />
                    )}
                    <span className="flex-1">{list.length} cobrança(s) · {fmtBRL(list.reduce((a, c) => a + (Number(c.valor) || 0), 0))}</span>
                </div>

                {loading ? (
                    <p className="text-center text-slate-500 py-16 text-sm">Carregando...</p>
                ) : list.length === 0 ? (
                    <p className="text-center text-slate-500 py-16 text-sm">Nenhuma cobrança neste filtro.</p>
                ) : (
                    <div className="divide-y divide-white/5">
                        {list.map(c => {
                            const etapa = c.etapa || 'a_cobrar';
                            const next = NEXT_ETAPA[etapa];
                            const overdue = isOverdue(c);
                            return (
                                <div key={c.id} className={`px-4 py-3 flex items-center gap-3 hover:bg-white/[0.03] transition-colors ${selected.has(c.id) ? 'bg-cyan-500/5' : ''}`}>
                                    {(canChangeStage || canCancel) && (
                                        <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} className="rounded border-slate-600 bg-slate-800 text-cyan-500 flex-shrink-0" />
                                    )}
                                    <button onClick={() => openDetail(c)} className="flex-1 min-w-0 text-left flex items-center gap-3">
                                        <span className="font-mono font-bold text-cyan-400 text-xs w-14 flex-shrink-0">A{c.numeroSequencial}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-slate-100 font-semibold truncate">{c.titulo}</p>
                                            <p className="text-[11px] text-slate-500 truncate">
                                                {c.empresaNome || 'Sem empresa'}
                                                {' · '}{(c.etapa || '') === 'a_faturar' && c.preferenciaNota ? (c.preferenciaNota === 'com_nota' ? 'Técnico indicou: com NF' : 'Técnico indicou: sem NF') : (TIPO_LABEL[c.faturamentoTipo] || 'Sem tipo')}
                                                {' · '}<span className={c.vhsysReceitaId ? '' : 'text-amber-400'}>{vhsysRef(c)}</span>
                                                {c.vhsysDivergencia && <span className="text-orange-400 font-bold"> · ⚠ {c.vhsysDivergencia}</span>}
                                                {(c.faturaNumeros || []).length > 1 && <span className="text-violet-300"> · Faturamento {c.faturaNumeros.join(', ')}</span>}
                                                {faturarAtrasado(c) && <span className="text-red-400 font-bold"> · aguardando há {diasAFaturar(c)} dias</span>}
                                                {' · '}Chamado {fmtDate(c.dataChamado)}
                                                {c.vencimento && <> · <span className={overdue ? 'text-red-400 font-bold' : ''}>Venc. {ymdToBR(c.vencimento)}{overdue ? ' (vencida)' : ''}</span></>}
                                                {!ticketIds.has(c.id) && ticketsLoaded && <span className="text-red-400"> · chamado removido</span>}
                                            </p>
                                        </div>
                                    </button>
                                    <span className="font-mono text-sm font-bold text-emerald-400 whitespace-nowrap">{fmtBRL(c.valor)}</span>
                                    <VhsysDot c={c} />
                                    <EtapaBadge etapa={etapa} />
                                    {etapa === 'a_faturar' && canFaturar && (
                                        <button
                                            disabled={busy}
                                            onClick={() => abrirFaturamento([c.id], 'faturar')}
                                            className="hidden sm:inline-block text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-lg border whitespace-nowrap text-violet-200 border-violet-500/40 hover:bg-white/5 disabled:opacity-50"
                                            title="Gerar o lançamento no VHSYS"
                                        >
                                            Faturar
                                        </button>
                                    )}
                                    {etapa === 'a_cobrar' && canSend ? (
                                        <button
                                            disabled={busy}
                                            onClick={() => abrirEnvio(c)}
                                            className={`hidden sm:inline-block text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-lg border whitespace-nowrap text-cyan-200 border-cyan-500/40 hover:bg-white/5 disabled:opacity-50 ${envioInfo(c).pode ? '' : 'opacity-50'}`}
                                            title={envioInfo(c).pode ? 'Enviar boleto e documentos ao cliente por e-mail' : envioInfo(c).motivo}
                                        >
                                            Enviar cobrança
                                        </button>
                                    ) : canChangeStage && next && (
                                        <button
                                            disabled={busy}
                                            onClick={() => confirmAndChange([c.id], next)}
                                            className={`hidden sm:inline-block text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-lg border whitespace-nowrap ${ETAPAS[next].color} ${ETAPAS[next].border} hover:bg-white/5 disabled:opacity-50`}
                                            title={`Marcar como ${ETAPAS[next].label}`}
                                        >
                                            Marcar {ETAPAS[next].label}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modal de detalhe */}
            {detailItem && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[130] flex items-center justify-center p-4" onClick={() => setDetail(null)}>
                    <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                        <header className="p-5 border-b border-slate-700 flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <p className="text-xs font-mono font-bold text-cyan-400">Chamado A{detailItem.numeroSequencial}</p>
                                <h3 className="text-lg font-bold text-white truncate">{detailItem.titulo}</h3>
                                <p className="text-xs text-slate-400">{detailItem.empresaNome}{detailItem.empresaRazaoSocial && detailItem.empresaRazaoSocial !== detailItem.empresaNome ? ` (${detailItem.empresaRazaoSocial})` : ''}</p>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                                {canOpenTickets && navigateTo && ticketIds.has(detailItem.id) && (
                                    <button
                                        onClick={() => openTicket(detailItem)}
                                        className="flex items-center gap-2 bg-slate-700 hover:bg-cyan-600 border border-slate-600 hover:border-cyan-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg transition-colors"
                                        title="Abrir o chamado completo na página de Chamados"
                                    >
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                        Ver Chamado
                                    </button>
                                )}
                                <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-white text-2xl font-bold leading-none">✕</button>
                            </div>
                        </header>

                        <div className="p-5 overflow-y-auto custom-scrollbar space-y-5">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                <div><span className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold">Valor</span><span className="font-mono font-bold text-emerald-400 text-lg">{fmtBRL(detailItem.valor)}</span></div>
                                <div><span className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold">Etapa</span><EtapaBadge etapa={detailItem.etapa || 'a_cobrar'} /></div>
                                <div><span className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold">Tipo</span>{(detailItem.etapa || '') === 'a_faturar' ? (detailItem.preferenciaNota === 'com_nota' ? 'Técnico indicou: com NF' : detailItem.preferenciaNota === 'sem_nota' ? 'Técnico indicou: sem NF' : 'A definir') : (TIPO_LABEL[detailItem.faturamentoTipo] || 'Sem tipo')}</div>
                                <div><span className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold">Situação no VHSYS</span><VhsysDot c={detailItem} withLabel /></div>
                                <div><span className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold">Status do chamado</span>{detailItem.statusChamado || '—'}</div>
                                <div><span className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold">Contato</span>{detailItem.clienteNome || '—'}</div>
                                <div><span className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold">Abertura do chamado</span>{fmtDate(detailItem.dataChamado)}</div>
                                <div><span className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold">Cobrado em</span>{fmtDate(detailItem.dataCobranca)}</div>
                                <div><span className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold">Recebido em</span>{fmtDate(detailItem.dataRecebimento)}{detailItem.valorRecebido != null && Number(detailItem.valorRecebido) !== Number(detailItem.valor) ? <span className="block text-[11px] text-amber-300 font-mono">{fmtBRL(detailItem.valorRecebido)} recebido</span> : null}{(detailItem.etapa === 'recebido' && (detailItem.contaBancariaNome || detailItem.formaPagamento)) ? <span className="block text-[11px] text-slate-400">{[detailItem.contaBancariaNome, detailItem.formaPagamento].filter(Boolean).join(' · ')}</span> : null}</div>
                            </div>

                            {/* Faturamento */}
                            {((detailItem.etapa || '') === 'a_faturar' || detailItem.faturaId) && (
                                <div className="bg-violet-500/5 border border-violet-500/30 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                                    {(detailItem.etapa || '') === 'a_faturar' ? (
                                        <>
                                            <span className="text-violet-200">Ainda não faturado{faturarAtrasado(detailItem) ? <b className="text-red-300"> · aguardando há {diasAFaturar(detailItem)} dias</b> : null}</span>
                                            {canFaturar && <button onClick={() => abrirFaturamento([detailItem.id], 'faturar')} disabled={busy} className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border border-violet-500/40 bg-violet-500/15 text-violet-200 hover:brightness-125 disabled:opacity-50">Faturar</button>}
                                        </>
                                    ) : (
                                        <>
                                            <span className="text-violet-200">
                                                {(detailItem.faturaNumeros || []).length > 1 ? <>Faturado junto com <b>{detailItem.faturaNumeros.filter(n => n !== `A${detailItem.numeroSequencial}`).join(', ')}</b></> : 'Faturado individualmente'}
                                                {detailItem.contaBancariaNome ? <span className="text-slate-400"> · {detailItem.contaBancariaNome}{detailItem.formaPagamento ? ` · ${detailItem.formaPagamento}` : ''}</span> : null}
                                            </span>
                                            {canFaturar && detailItem.etapa !== 'recebido' && detailItem.etapa !== 'cancelado' && (
                                                <button onClick={() => setDesfazerDlg({ id: detailItem.id, numeros: detailItem.faturaNumeros || [`A${detailItem.numeroSequencial}`] })} disabled={busy} className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border border-slate-500/40 bg-slate-500/10 text-slate-300 hover:brightness-125 disabled:opacity-50">Desfazer faturamento</button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Painel VHSYS (só depois de faturado) */}
                            {(detailItem.etapa || '') !== 'a_faturar' && (
                            <div className="bg-slate-900/50 border border-slate-700/60 rounded-xl p-4 space-y-3 text-sm">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">VHSYS</span>
                                    <div className="flex items-center gap-3 text-[11px] text-slate-500">
                                        {detailItem.vhsysUltimaSync && <span>Sincronizado em {fmtDateTime(detailItem.vhsysUltimaSync)}</span>}
                                        {canSync && (
                                            <button onClick={() => runVhsysSync([detailItem.id])} disabled={vhsysSyncing} className="text-emerald-400 hover:text-emerald-300 font-bold uppercase tracking-wider disabled:opacity-50">
                                                {vhsysSyncing ? 'Consultando...' : 'Sincronizar esta'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <p className="text-slate-300">{vhsysRef(detailItem)}{detailItem.vhsysVinculoAuto ? <span className="text-slate-500"> (vinculada automaticamente)</span> : null}</p>
                                <DocumentosFiscais ticketId={detailItem.id} />
                                {detailItem.vhsysErro && <p className="text-red-300 text-xs">⚠ {detailItem.vhsysErro}</p>}
                                {detailItem.vhsysDivergencia && (
                                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 space-y-2">
                                        <p className="text-orange-300 text-xs font-bold">⚠ {detailItem.vhsysDivergencia}</p>
                                        {(canChangeStage || canCancel) && (
                                            <div className="flex flex-wrap gap-2">
                                                {detailItem.etapa === 'recebido' && canChangeStage && (
                                                    <>
                                                        <button disabled={busy} onClick={() => confirmAndChange([detailItem.id], 'recebido', { forcarVhsys: true })} className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:brightness-125 disabled:opacity-50">Dar baixa no VHSYS</button>
                                                        <button disabled={busy} onClick={() => changeEtapaLocal([detailItem], 'cobrado', 'Corrigido: ainda não recebido (VHSYS em aberto)').then(() => showToast('Voltou para Cobrança Enviada.'))} className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:brightness-125 disabled:opacity-50">Voltar para Cobrança Enviada</button>
                                                    </>
                                                )}
                                                {detailItem.etapa === 'cancelado' && canCancel && (
                                                    <button disabled={busy} onClick={() => confirmAndChange([detailItem.id], 'cancelado', { forcarVhsys: true })} className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 hover:brightness-125 disabled:opacity-50">Enviar para a lixeira do VHSYS</button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {canEditDetails && (
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">ID da conta a receber no VHSYS (vínculo manual)</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={detailForm.vhsysReceitaId || ''}
                                            onChange={e => setDetailForm(f => ({ ...f, vhsysReceitaId: e.target.value }))}
                                            placeholder="Ex.: 142517471"
                                            className="w-full md:w-64 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white font-mono outline-none focus:ring-1 focus:ring-cyan-500"
                                        />
                                    </div>
                                )}
                            </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {(detailItem.etapa || '') !== 'a_faturar' && <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Vencimento</label>
                                    <input type="date" disabled={!canEditDetails} value={detailForm.vencimento} onChange={e => setDetailForm(f => ({ ...f, vencimento: e.target.value }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500 disabled:opacity-60" />
                                </div>}
                                <div className={(detailItem.etapa || '') === 'a_faturar' ? 'md:col-span-3' : 'md:col-span-2'}>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Observações</label>
                                    <textarea rows={2} disabled={!canEditDetails} value={detailForm.observacoes} onChange={e => setDetailForm(f => ({ ...f, observacoes: e.target.value }))} placeholder="Ex.: boleto enviado por e-mail, combinado pagar dia 10..." className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500 disabled:opacity-60" />
                                </div>
                            </div>
                            {canEditDetails && (
                                <div className="flex justify-end">
                                    <button onClick={saveDetail} disabled={busy} className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg disabled:opacity-50">Salvar detalhes</button>
                                </div>
                            )}

                            {['a_cobrar', 'cobrado'].includes(detailItem.etapa || 'a_cobrar') && (() => {
                                const info = envioInfo(detailItem);
                                const envios = [...(detailItem.envios || [])].sort((a, b) => String(b.em).localeCompare(String(a.em)));
                                return (
                                    <div className="bg-cyan-500/5 border border-cyan-500/30 rounded-xl p-4 space-y-3 text-sm">
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <span className="block text-[10px] uppercase tracking-wider text-cyan-300 font-bold">Envio ao cliente</span>
                                                <span className="text-slate-300 text-xs">
                                                    {envios.length ? `Último envio em ${new Date(envios[0].em).toLocaleString('pt-BR')} para ${(envios[0].para || []).join(', ')}` : 'Ainda não enviada ao cliente.'}
                                                </span>
                                            </div>
                                            {canSend && (
                                                <button
                                                    disabled={busy || !info.pode}
                                                    onClick={() => abrirEnvio(detailItem)}
                                                    title={info.pode ? 'Enviar boleto e documentos ao cliente por e-mail' : info.motivo}
                                                    className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border border-cyan-500/40 bg-cyan-500/15 text-cyan-200 hover:brightness-125 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {envios.length ? 'Reenviar cobrança' : 'Enviar cobrança'}
                                                </button>
                                            )}
                                        </div>
                                        {!info.pode && <p className="text-[11px] text-amber-300">⚠ {info.motivo}</p>}
                                        {envios.length > 1 && (
                                            <ul className="text-[11px] text-slate-400 space-y-0.5">
                                                {envios.slice(1, 6).map((e, i) => <li key={i}>{new Date(e.em).toLocaleString('pt-BR')} · {(e.para || []).join(', ')} · por {e.por}</li>)}
                                            </ul>
                                        )}
                                    </div>
                                );
                            })()}

                            {(canChangeStage || canCancel) && (
                                <div>
                                    <span className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Mover para</span>
                                    <div className="flex flex-wrap gap-2">
                                        {ETAPA_ORDER.filter(e => e !== 'a_faturar' && e !== (detailItem.etapa || 'a_cobrar')).filter(e => (detailItem.etapa || '') !== 'a_faturar' || e === 'cancelado').filter(e => e === 'cancelado' ? canCancel : canChangeStage).map(e => (
                                            <button key={e} disabled={busy} onClick={() => confirmAndChange([detailItem.id], e)} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border ${ETAPAS[e].color} ${ETAPAS[e].bg} ${ETAPAS[e].border} hover:brightness-125 disabled:opacity-50`}>
                                                {ETAPAS[e].label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <span className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Histórico</span>
                                <div className="space-y-2">
                                    {[...(detailItem.historico || [])].sort((a, b) => String(b.em).localeCompare(String(a.em))).map((h, i) => (
                                        <div key={i} className="text-xs bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2">
                                            <EtapaBadge etapa={h.etapa} />
                                            {h.de && <span className="text-slate-500">de {ETAPAS[h.de]?.label || h.de}</span>}
                                            <span className="text-slate-400">{fmtDateTime(h.em)}</span>
                                            <span className="text-slate-500">· {h.por}</span>
                                            {h.nota && <span className="text-slate-300 w-full">{h.nota}</span>}
                                        </div>
                                    ))}
                                    {(!detailItem.historico || detailItem.historico.length === 0) && <p className="text-xs text-slate-500">Sem histórico.</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Janela de faturamento / agrupamento */}
            {envioDlg && (() => {
                const d = envioDlg;
                const novos = [...new Set([...d.para, ...separarEmails(d.paraInput)])].filter(e => EMAIL_RE.test(e) && !d.cadastrados.includes(e));
                const chk = (key, label, desc) => (
                    <label className="flex items-start gap-2 text-sm text-slate-200 cursor-pointer">
                        <input type="checkbox" checked={!!d.anexos[key]} onChange={e => setEnvioDlg(x => ({ ...x, anexos: { ...x.anexos, [key]: e.target.checked } }))} className="mt-0.5 rounded border-slate-600 bg-slate-800 text-cyan-500" />
                        <span>{label}{desc && <span className="block text-[11px] text-slate-500">{desc}</span>}</span>
                    </label>
                );
                return (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[150] flex items-center justify-center p-4 animate-fadeIn" onClick={() => !busy && setEnvioDlg(null)}>
                        <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                            <div className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
                                <div>
                                    <h3 className="text-lg font-bold text-white">{d.reenvio ? 'Reenviar cobrança' : 'Enviar cobrança'}</h3>
                                    <p className="text-sm text-slate-400">{d.empresaNome} · {d.numeros.join(', ')}</p>
                                </div>

                                <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Para *</label>
                                    <EmailChips
                                        value={d.para}
                                        input={d.paraInput}
                                        erro={d.paraErro}
                                        placeholder="financeiro@cliente.com (Enter para adicionar)"
                                        onInput={v => setEnvioDlg(x => ({ ...x, paraInput: v, paraErro: '' }))}
                                        onChange={(lista, resto) => setEnvioDlg(x => ({ ...x, para: lista, paraInput: resto || '', paraErro: resto ? `E-mail inválido: ${resto}` : '' }))}
                                    />
                                    {d.paraErro && <p className="text-[11px] text-red-300 mt-1">{d.paraErro}</p>}
                                    {d.cadastrados.length === 0 && (
                                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                                            <span className="text-amber-300">Este cliente não tem e-mails para faturamento cadastrados.</span>
                                            {d.emailCadastro && !d.para.includes(d.emailCadastro) && (
                                                <button type="button" onClick={() => setEnvioDlg(x => ({ ...x, para: [...x.para, x.emailCadastro], paraErro: '' }))} className="font-bold uppercase tracking-wider px-2 py-1 rounded-md border border-cyan-500/40 text-cyan-200 hover:bg-white/5">
                                                    Usar e-mail do cadastro ({d.emailCadastro})
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {novos.length > 0 && d.empresaId && (
                                        <label className="mt-2 flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                                            <input type="checkbox" checked={d.salvar} onChange={e => setEnvioDlg(x => ({ ...x, salvar: e.target.checked }))} className="rounded border-slate-600 bg-slate-800 text-cyan-500" />
                                            Salvar {novos.length === 1 ? novos[0] : `${novos.length} e-mails`} em "E-mails para Faturamento" do cliente
                                        </label>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Cópia (CC)</label>
                                    <EmailChips
                                        value={d.cc}
                                        input={d.ccInput}
                                        erro={d.ccErro}
                                        placeholder="Opcional"
                                        onInput={v => setEnvioDlg(x => ({ ...x, ccInput: v, ccErro: '' }))}
                                        onChange={(lista, resto) => setEnvioDlg(x => ({ ...x, cc: lista, ccInput: resto || '', ccErro: resto ? `E-mail inválido: ${resto}` : '' }))}
                                    />
                                    {d.ccErro && <p className="text-[11px] text-red-300 mt-1">{d.ccErro}</p>}
                                    <p className="text-[11px] text-slate-500 mt-1">Uma cópia oculta sempre vai para faturamento@arcusinformatica.com.br, e as respostas do cliente chegam lá.</p>
                                </div>

                                <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Assunto</label>
                                    <input type="text" value={d.assunto} maxLength={200} onChange={e => setEnvioDlg(x => ({ ...x, assunto: e.target.value }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500" />
                                </div>

                                <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Mensagem adicional</label>
                                    <textarea rows={3} value={d.mensagem} maxLength={3000} onChange={e => setEnvioDlg(x => ({ ...x, mensagem: e.target.value }))} placeholder="Opcional: aparece no corpo do e-mail, antes dos dados da cobrança." className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500 placeholder:text-slate-600" />
                                </div>

                                <div className="space-y-2">
                                    <span className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold">Anexos</span>
                                    {d.temBoleto
                                        ? chk('boleto', 'Boleto (PDF)', 'Se o PDF não puder ser baixado do VHSYS, o e-mail leva o link do boleto.')
                                        : <p className="text-[11px] text-slate-400">Sem boleto: forma de pagamento <b className="text-slate-200">{d.forma || 'não informada'}</b>. O e-mail informa a forma de pagamento; use a mensagem adicional para passar a chave PIX ou os dados bancários.</p>}
                                    {!d.comNota && !d.temBoleto && <p className="text-[11px] text-slate-500">Nenhum anexo: o e-mail vai só com o resumo da cobrança.</p>}
                                    {d.comNota && chk('danfse', 'DANFSe (PDF no layout Arcus)', 'Gerado agora a partir do XML e salvo no Storage.')}
                                    {d.comNota && chk('xml', 'XML da NFS-e')}
                                </div>
                            </div>
                            <div className="p-4 border-t border-slate-700 flex items-center justify-end gap-3">
                                {envioFase && <span className="text-xs text-cyan-300 mr-auto">{envioFase}</span>}
                                <button disabled={busy} onClick={() => setEnvioDlg(null)} className="text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:text-white disabled:opacity-50">Cancelar</button>
                                <button disabled={busy} onClick={enviarCobranca} className="text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white disabled:opacity-50">
                                    {busy ? 'Enviando...' : (d.reenvio ? 'Reenviar' : 'Enviar')}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {fatDlg && (() => {
                const items = fatDlg.ids.map(id => cobrancas.find(c => c.id === id)).filter(Boolean).sort((a, b) => (a.numeroSequencial || 0) - (b.numeroSequencial || 0));
                const total = items.reduce((acc, c) => acc + (Number(c.valor) || 0), 0);
                const agrupar = fatDlg.modo === 'agrupar';
                const pronto = fatDlg.comNota !== null && fatDlg.vencimento && fatDlg.idBanco && fatDlg.formaPagamento;
                const opt = (on) => `flex-1 text-xs font-bold uppercase tracking-wider px-3 py-2.5 rounded-lg border transition-colors ${on ? 'border-violet-400 bg-violet-500/20 text-white' : 'border-slate-600 text-slate-400 hover:text-white'}`;
                return (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[150] flex items-center justify-center p-4 animate-fadeIn" onClick={() => !busy && setFatDlg(null)}>
                        <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                            <div className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
                                <div>
                                    <h3 className="text-lg font-bold text-white">{agrupar ? 'Agrupar em um único lançamento' : (items.length > 1 ? 'Faturar fechamento' : 'Faturar cobrança')}</h3>
                                    <p className="text-sm text-slate-400">{items[0]?.empresaNome} · {items.length} chamado(s) · <span className="font-mono font-bold text-emerald-400">{fmtBRL(total)}</span></p>
                                </div>
                                <div className="bg-slate-900/50 border border-slate-700 rounded-lg divide-y divide-slate-700/60 max-h-40 overflow-y-auto custom-scrollbar">
                                    {items.map(c => (
                                        <div key={c.id} className="px-3 py-2 flex items-center gap-3 text-xs">
                                            <span className="font-mono font-bold text-cyan-400 w-12">A{c.numeroSequencial}</span>
                                            <span className="flex-1 truncate text-slate-200">{c.titulo}</span>
                                            {c.preferenciaNota && <span className={`text-[10px] font-bold uppercase ${c.preferenciaNota === 'com_nota' ? 'text-cyan-300' : 'text-amber-300'}`}>{c.preferenciaNota === 'com_nota' ? 'Pede NF' : 'Sem NF'}</span>}
                                            <span className="font-mono text-emerald-400">{fmtBRL(c.valor)}</span>
                                        </div>
                                    ))}
                                </div>
                                {agrupar && (
                                    <p className="text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                                        Os lançamentos individuais destes chamados serão removidos do VHSYS (contas em aberto vão para a lixeira; NFS-e em rascunho é excluída) e um lançamento único será criado. Se algum tiver NF emitida ou conta paga, nada é feito.
                                    </p>
                                )}
                                <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Nota fiscal *</label>
                                    <div className="flex gap-2">
                                        <button type="button" onClick={() => setFatDlg(d => ({ ...d, comNota: true }))} className={opt(fatDlg.comNota === true)}>Com NF (rascunho NFS-e)</button>
                                        <button type="button" onClick={() => setFatDlg(d => ({ ...d, comNota: false }))} className={opt(fatDlg.comNota === false)}>Sem NF (conta a receber)</button>
                                    </div>
                                    {(() => { const ps = new Set(items.map(c => c.preferenciaNota).filter(Boolean)); return ps.size > 1 ? <p className="text-[11px] text-amber-300 mt-1">⚠ Os técnicos indicaram preferências diferentes de NF para estes chamados.</p> : null; })()}
                                    {fatDlg.comNota === true && <p className="text-[11px] text-slate-400 mt-1">A NFS-e vai como rascunho para o VHSYS. Depois de emitir e lançar as contas por lá, a Intranet aplica automaticamente a conta, a forma e o vencimento escolhidos abaixo.</p>}
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Vencimento *</label>
                                        <input type="date" value={fatDlg.vencimento} min={today} onChange={e => setFatDlg(d => ({ ...d, vencimento: e.target.value }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-violet-500 [color-scheme:dark]" />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Forma de pagamento *</label>
                                        <select value={fatDlg.formaPagamento} onChange={e => setFatDlg(d => ({ ...d, formaPagamento: e.target.value }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-violet-500">
                                            <option value="">Selecione</option>
                                            {formasPagamento.map(f => <option key={f} value={f}>{f}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Conta bancária *</label>
                                        <select value={fatDlg.idBanco} onChange={e => setFatDlg(d => ({ ...d, idBanco: e.target.value }))} disabled={contasVhsys === null} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-violet-500 disabled:opacity-60">
                                            <option value="">{contasVhsys === null ? 'Carregando contas do VHSYS...' : 'Selecione a conta que vai receber'}</option>
                                            {(contasVhsys || []).map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
                                        </select>
                                        {contasErro && <p className="text-[11px] text-red-300 mt-1">{contasErro}</p>}
                                        {fatDlg.formaPagamento && fatDlg.comNota === false && (
                                            <p className="text-[11px] text-slate-400 mt-1">
                                                {usaBoleto(fatDlg.formaPagamento)
                                                    ? 'Com Boleto, a conta bancária precisa estar configurada para emitir boletos no VHSYS.'
                                                    : `Lançado como conta a receber simples (sem boleto), para pagamento via ${fatDlg.formaPagamento}.`}
                                            </p>
                                        )}
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Observação (vai na descrição do lançamento)</label>
                                        <input type="text" maxLength={300} value={fatDlg.obs} onChange={e => setFatDlg(d => ({ ...d, obs: e.target.value }))} placeholder="Opcional" className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-violet-500" />
                                    </div>
                                </div>
                            </div>
                            <div className="px-6 py-4 bg-slate-900/40 border-t border-slate-700 flex justify-end gap-3">
                                <button onClick={() => setFatDlg(null)} disabled={busy} className="bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-lg transition-colors disabled:opacity-40">Voltar</button>
                                <button onClick={enviarFaturamento} disabled={busy || !pronto} className="bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-lg transition-colors disabled:opacity-40">
                                    {busy ? 'Enviando ao VHSYS...' : (agrupar ? 'Agrupar' : 'Faturar')}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {desfazerDlg && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[150] flex items-center justify-center p-4 animate-fadeIn" onClick={() => !busy && setDesfazerDlg(null)}>
                    <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="p-6 space-y-3">
                            <h3 className="text-lg font-bold text-white">Desfazer faturamento</h3>
                            <p className="text-sm text-slate-300">O lançamento de <b>{desfazerDlg.numeros.join(', ')}</b> será removido do VHSYS e as cobranças voltam para <b>A Faturar</b>.</p>
                            <p className="text-[11px] text-slate-400">Só é possível se a conta não estiver paga e a NF não tiver sido emitida.</p>
                        </div>
                        <div className="px-6 py-4 bg-slate-900/40 border-t border-slate-700 flex justify-end gap-3">
                            <button onClick={() => setDesfazerDlg(null)} disabled={busy} className="bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-lg">Voltar</button>
                            <button onClick={desfazerFaturamento} disabled={busy} className="bg-red-600 hover:bg-red-500 text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-lg disabled:opacity-40">{busy ? 'Desfazendo...' : 'Desfazer'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Janela de confirmação (substitui confirm/prompt do navegador) */}
            {confirmDlg && (() => {
                const cfg = ETAPAS[confirmDlg.etapa];
                const isCancel = confirmDlg.etapa === 'cancelado';
                const isRecebido = confirmDlg.etapa === 'recebido';
                const qtd = confirmDlg.ids.length;
                const comVhsys = confirmDlg.ids.filter(id => cobrancas.find(c => c.id === id)?.vhsysReceitaId).length;
                const dlgItems = confirmDlg.ids.map(id => cobrancas.find(c => c.id === id)).filter(Boolean);
                const afetaVhsys = isRecebido || isCancel || dlgItems.some(c => c.etapa === 'recebido');
                return (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[150] flex items-center justify-center p-4 animate-fadeIn" onClick={() => setConfirmDlg(null)}>
                        <div className={`bg-slate-800 border border-slate-700 rounded-2xl w-full ${confirmDlg.etapa === 'recebido' ? 'max-w-lg' : 'max-w-md'} shadow-2xl overflow-hidden`} onClick={e => e.stopPropagation()}>
                            <div className="p-6 space-y-4">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border ${isCancel ? 'bg-red-500/10 border-red-500/30 text-red-400' : `${cfg.bg} ${cfg.border} ${cfg.color}`}`}>
                                        {isCancel ? (
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                        ) : (
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-white">{isCancel ? (confirmDlg.forcarVhsys ? 'Enviar para a lixeira do VHSYS' : 'Cancelar cobrança') : (confirmDlg.forcarVhsys ? 'Dar baixa no VHSYS' : `Mover para ${cfg.label}`)}</h3>
                                        <p className="text-sm text-slate-400">
                                            {qtd} cobrança(s) · <span className="font-mono font-bold text-emerald-400">{fmtBRL(confirmDlg.total)}</span>
                                        </p>
                                    </div>
                                </div>
                                {isRecebido && (
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Data do pagamento *</label>
                                            <input
                                                type="date"
                                                value={confirmDlg.dataPagamento}
                                                max={today}
                                                onChange={e => setConfirmDlg(d => ({ ...d, dataPagamento: e.target.value }))}
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-emerald-500 [color-scheme:dark]"
                                            />
                                        </div>
                                        {qtd === 1 ? (
                                            <div>
                                                <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Valor recebido *</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={confirmDlg.valorPago}
                                                    onChange={e => setConfirmDlg(d => ({ ...d, valorPago: e.target.value }))}
                                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white font-mono outline-none focus:ring-1 focus:ring-emerald-500"
                                                />
                                            </div>
                                        ) : (
                                            <p className="text-[11px] text-slate-400 self-end pb-2">Cada cobrança será baixada pelo seu valor total.</p>
                                        )}
                                        <div className="col-span-2">
                                            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Conta bancária *</label>
                                            <select
                                                value={confirmDlg.idBanco}
                                                onChange={e => setConfirmDlg(d => ({ ...d, idBanco: e.target.value }))}
                                                disabled={contasVhsys === null}
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-60"
                                            >
                                                <option value="">{contasVhsys === null ? 'Carregando contas do VHSYS...' : 'Selecione a conta que recebeu'}</option>
                                                {(contasVhsys || []).map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
                                                {confirmDlg.idBanco && contasVhsys && !contasVhsys.some(b => b.id === confirmDlg.idBanco) && <option value={confirmDlg.idBanco}>Conta {confirmDlg.idBanco}</option>}
                                            </select>
                                            {contasErro && <p className="text-[11px] text-red-300 mt-1">{contasErro}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Forma de pagamento *</label>
                                            <select
                                                value={confirmDlg.formaPagamento}
                                                onChange={e => setConfirmDlg(d => ({ ...d, formaPagamento: e.target.value }))}
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-emerald-500"
                                            >
                                                <option value="">Selecione</option>
                                                {formasPagamento.map(f => <option key={f} value={f}>{f}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Observação</label>
                                            <input
                                                type="text"
                                                maxLength={255}
                                                value={confirmDlg.obsPagamento}
                                                onChange={e => setConfirmDlg(d => ({ ...d, obsPagamento: e.target.value }))}
                                                placeholder="Opcional"
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-emerald-500"
                                            />
                                        </div>
                                    </div>
                                )}
                                {afetaVhsys && comVhsys > 0 && (
                                    <p className="text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
                                        {isRecebido ? `A baixa também será feita no VHSYS (${comVhsys} conta(s)).`
                                            : isCancel ? `${comVhsys} conta(s) irá(ão) para a lixeira do VHSYS.`
                                            : `A baixa será desfeita no VHSYS (${comVhsys} conta(s)).`}
                                    </p>
                                )}
                                {isCancel && (
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Motivo do cancelamento *</label>
                                        <textarea
                                            autoFocus
                                            rows={3}
                                            value={confirmDlg.motivo}
                                            onChange={e => setConfirmDlg(d => ({ ...d, motivo: e.target.value }))}
                                            placeholder="Ex.: valor lançado por engano, cliente com contrato..."
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-red-500"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="px-6 py-4 bg-slate-900/40 border-t border-slate-700 flex justify-end gap-3">
                                <button onClick={() => setConfirmDlg(null)} className="bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-lg transition-colors">Voltar</button>
                                <button
                                    onClick={submitConfirm}
                                    disabled={busy || (isCancel && !confirmDlg.motivo.trim()) || (isRecebido && (!confirmDlg.dataPagamento || !confirmDlg.idBanco || !confirmDlg.formaPagamento || (qtd === 1 && !(Number(confirmDlg.valorPago) > 0))))}
                                    className={`text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-lg transition-colors disabled:opacity-40 ${isCancel ? 'bg-red-600 hover:bg-red-500' : 'bg-cyan-600 hover:bg-cyan-500'}`}
                                >
                                    {isCancel ? 'Confirmar cancelamento' : 'Confirmar'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {toast && (
                <div className={`fixed bottom-6 right-6 z-[160] bg-slate-900 border text-sm px-4 py-3 rounded-xl shadow-2xl animate-fadeIn max-w-sm ${toast.type === 'error' ? 'border-red-500/50 text-red-300' : 'border-cyan-500/40 text-cyan-200'}`}>{toast.msg}</div>
            )}
        </div>
    );
}
